import { useState } from "react";
import { FileText, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadFile } from "../../lib/api";
import CvSourceTabs from "./CvSourceTabs";
import CvDownloadPanel from "./CvDownloadPanel";
import EvidenceLibrary from "./EvidenceLibrary";
import VoiceRefine from "./VoiceRefine";

const TABS = [["cv", "Your CV"], ["evidence", "Evidence Library"], ["voice", "Add experience by voice"]];

function YourCv({ baseCv, onUploaded }) {
  return (
    <div className="space-y-5">
      {baseCv && (
        <div className="gi-card p-5 sm:p-6" data-testid="parsed-skills-container">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center"><FileText className="w-5 h-5 text-brand" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-ink flex items-center gap-2 truncate">{baseCv.original_filename} <CheckCircle2 className="w-4 h-4 text-success" aria-label="Saved" /></div>
              <div className="text-sm text-faint">Saved {String(baseCv.created_at || "").slice(0, 10)} · used for every new application</div>
            </div>
            {baseCv.has_file && (
              <button data-testid="download-original-cv-button" onClick={() => downloadFile("/cv/file", baseCv.original_filename || "my_cv").catch(() => toast.error("Download didn't work. Please try again."))} className="gi-btn gi-btn-secondary gi-btn-sm">
                <Download className="w-4 h-4" /> Original file
              </button>
            )}
          </div>
          <details>
            <summary className="cursor-pointer text-sm font-medium text-brand">See the text we read from your CV</summary>
            <pre className="mt-3 max-h-64 overflow-y-auto rounded-xl bg-canvas border border-line p-4 text-sm text-body whitespace-pre-wrap leading-relaxed font-sans">{baseCv.text?.slice(0, 4000)}</pre>
          </details>
        </div>
      )}
      {baseCv?.structured && (
        <div className="gi-card p-5 sm:p-6" data-testid="linkedin-cv-panel">
          <h2 className="font-display text-lg font-bold text-ink">{baseCv.source === "profile" ? "Your CV, built from your profile" : "Your CV, built from LinkedIn"}</h2>
          <p className="text-[15px] text-body mt-1 mb-4">Pick a design and download it, or tailor it to a job in CV Studio.</p>
          <CvDownloadPanel cv={baseCv.structured} testid="base-cv-download" pathFor={(fmt, d) => `/cv/download.${fmt}?template=${d}`} />
        </div>
      )}
      <div>
        <h2 className="font-display text-lg font-bold text-ink mb-3">{baseCv ? "Replace your CV" : "Add your CV"}</h2>
        <CvSourceTabs testid="mycv-source" onDone={onUploaded} uploadLabel={baseCv ? "Drop your updated CV here or tap to choose" : undefined} />
      </div>
    </div>
  );
}

export default function MyCv({ baseCv, onUploaded, initialTab = "cv" }) {
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="af-fade-up" data-testid="my-cv-view">
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">My CV & evidence</h1>
      <p className="text-body mt-1 mb-6">Everything we use to build your applications, in one place.</p>
      <div role="tablist" aria-label="My CV sections" className="flex gap-1 p-1 rounded-xl bg-subtle mb-6 overflow-x-auto">
        {TABS.map(([id, l]) => (
          <button key={id} role="tab" aria-selected={tab === id} data-testid={`mycv-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 min-h-[40px] rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${tab === id ? "bg-surface text-ink shadow-card" : "text-body hover:text-ink"}`}>{l}</button>
        ))}
      </div>
      {tab === "cv" && <YourCv baseCv={baseCv} onUploaded={onUploaded} />}
      {tab === "evidence" && <EvidenceLibrary />}
      {tab === "voice" && <VoiceRefine baseCv={baseCv} onNeedCv={() => setTab("cv")} onApplied={onUploaded} />}
    </div>
  );
}
